/**
 * Reiter „Überblick“ im Seitenpanel: Kerndaten, Termine, Ausführungsort, die nächste eigene
 * Niederlassung und die Karte.
 *
 * Ausführungsort und Niederlassung stehen **getrennt** und so beschriftet: die Niederlassung ist der
 * eigene Standort, der den Auftrag bedienen würde, nicht der Ort der Leistung — im alten Panel lasen
 * sich beide als eine Angabe. Einen Korrigieren-Knopf am Ausführungsort gibt es nicht, weil der
 * Server keinen Weg hat, ihn zu ändern.
 *
 * Die Karte zeichnet die Ebenen, die der Server fertig liefert (`tender.locate`); die Legende nennt
 * genau diese Ebenen mit ihrem Namen, statt eine eigene Liste zu behaupten.
 */
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { ExternalLink, Loader2, Maximize2, MapPin } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import LocationMap from "../../Map";
import type { TenderBoardRow, TenderLocationResult, TenderPanelFacts } from "../../TenderSearchProfileAPI";
import { TenderDateTimeline } from "./TenderDateTimeline";
import {
  formatPerformanceLocation,
  performanceLocationsState,
  publishedPlaceFallback,
} from "./tenderPerformanceLocations";

const Section = ({ title, children, aside }: { title: string; children: React.ReactNode; aside?: React.ReactNode }) => (
  <section>
    <div className="mb-2 flex items-baseline justify-between gap-2">
      <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{title}</h3>
      {aside}
    </div>
    {children}
  </section>
);

/** Legend swatch per layer key the server draws (tri-server `tenderLocationLayers`, colours from
 *  `crmMapElements`: the tender in #C2185B, the branches in #00695C). */
const swatch = (key: string) =>
  key === "searchArea" ? (
    <span className="inline-block h-3 w-3 rounded-full border-2 border-dashed border-[#C2185B]" aria-hidden />
  ) : key === "tenderArea" ? (
    <span className="inline-block h-3 w-3 rounded-sm border border-[#C2185B] bg-[#C2185B]/20" aria-hidden />
  ) : key === "projects" ? (
    <span className="inline-block h-2.5 w-2.5 rounded-full bg-slate-500" aria-hidden />
  ) : key === "locations" ? (
    <span className="inline-block h-2.5 w-2.5 rounded-full bg-[#00695C]" aria-hidden />
  ) : (
    <MapPin size={12} className="text-[#C2185B]" aria-hidden />
  );

export function TenderPanelOverview({
  row,
  facts,
  location,
  locationLoading,
  people,
  busy,
  onAddDate,
  onRemoveDate,
  onReviewConflict,
}: {
  row: TenderBoardRow;
  facts: TenderPanelFacts | null;
  location: TenderLocationResult | null | undefined;
  locationLoading: boolean;
  people: Record<string, string>;
  busy: boolean;
  onAddDate: (date: { label: string; at?: string; note?: string }) => Promise<unknown>;
  onRemoveDate: (key: string) => Promise<unknown>;
  onReviewConflict: () => void;
}) {
  const { t } = useTranslation();
  const [mapLarge, setMapLarge] = useState(false);
  const locationsState = performanceLocationsState(row);
  const publishedPlace = publishedPlaceFallback(row);
  const mapResource = (location?.__resources ?? []).find((resource) => resource.type === "map") ?? null;
  const layers: Record<string, { name?: string }> = mapResource?.data?.layers ?? {};
  const nearest = location?.nearest ?? null;

  const facts_ = [
    { label: t("tenderBoard.panel.facts.leistung"), value: facts?.leistung, long: true },
    { label: t("tenderBoard.panel.facts.laufzeit"), value: facts?.laufzeit },
    { label: t("tenderBoard.panel.facts.verfahren"), value: facts?.verfahren },
    { label: t("tenderBoard.panel.facts.vergabenummer"), value: facts?.vergabenummer },
    { label: t("tenderBoard.panel.facts.publicationNumber"), value: facts?.publicationNumber ?? row.publicationNumber },
  ].filter((fact) => fact.value);

  const map = (height: number) =>
    mapResource ? (
      <LocationMap
        query={null}
        height={height}
        hideOverlay
        requireModifierForScrollZoom
        layers={mapResource.data?.layers}
        options={mapResource.options}
      />
    ) : null;

  return (
    <div className="space-y-6" data-testid="tender-panel-overview">
      <Section title={t("tenderBoard.panel.facts.title")}>
        {facts === null ? (
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Loader2 size={12} className="animate-spin" aria-hidden /> {t("tenderBoard.loading")}
          </div>
        ) : (
          <table className="w-full text-sm">
            <tbody>
              {facts_.map((fact) => (
                <tr key={fact.label} className="border-b border-border last:border-0">
                  <th scope="row" className="w-36 py-1.5 pr-3 text-left align-top font-normal text-muted-foreground">
                    {fact.label}
                  </th>
                  <td className={`py-1.5 align-top ${fact.long ? "whitespace-pre-line" : ""}`}>
                    {fact.long && String(fact.value).length > 400 ? `${String(fact.value).slice(0, 400)} …` : fact.value}
                  </td>
                </tr>
              ))}
              {facts.plattform && (
                <tr>
                  <th scope="row" className="w-36 py-1.5 pr-3 text-left align-top font-normal text-muted-foreground">
                    {t("tenderBoard.panel.facts.plattform")}
                  </th>
                  <td className="py-1.5">
                    <a
                      href={facts.plattform.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 underline underline-offset-2"
                    >
                      {facts.plattform.host} <ExternalLink size={12} aria-hidden />
                    </a>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </Section>

      <Section
        title={t("tenderBoard.dates.title")}
        aside={
          row.analysisDatesUpdatedAt ? (
            <span className="text-[11px] text-muted-foreground">
              {t("tenderBoard.dates.analysedAt", {
                at: new Date(row.analysisDatesUpdatedAt).toLocaleString("de-DE", {
                  day: "2-digit",
                  month: "2-digit",
                  hour: "2-digit",
                  minute: "2-digit",
                }),
              })}
            </span>
          ) : null
        }
      >
        <TenderDateTimeline
          dates={row.dates ?? []}
          conflicts={row.dateConflicts ?? []}
          people={people}
          busy={busy}
          onAdd={onAddDate}
          onRemove={onRemoveDate}
          onReviewConflict={onReviewConflict}
        />
      </Section>

      <Section title={t("tenderBoard.panel.place.title")}>
        {locationsState === "found" ? (
          <ol className="ml-4 list-decimal space-y-0.5 text-sm">
            {(row.performanceLocations ?? []).map((place, index) => (
              <li key={index}>{formatPerformanceLocation(place)}</li>
            ))}
          </ol>
        ) : (
          <div className="text-sm">
            {publishedPlace ? (
              <div>{t("tenderBoard.locations.fromNotice", { place: publishedPlace })}</div>
            ) : location?.location?.label ? (
              <div>{location.location.label}</div>
            ) : null}
            <div className="mt-1 text-xs text-muted-foreground">
              {locationsState === "none-found" ? t("tenderBoard.locations.none") : t("tenderBoard.locations.notAnalysed")}
            </div>
          </div>
        )}
        {location?.precisionNote && <div className="mt-1 text-xs text-amber-700">{location.precisionNote}</div>}
      </Section>

      <Section title={t("tenderBoard.panel.place.branchTitle")}>
        {locationLoading ? (
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Loader2 size={12} className="animate-spin" aria-hidden /> {t("tenderBoard.loading")}
          </div>
        ) : nearest ? (
          <div className="flex items-start justify-between gap-3 rounded-md border border-border px-3 py-2 text-sm">
            <div>
              <div className="font-medium">{nearest.label}</div>
              {(nearest.postalCode || nearest.city) && (
                <div className="text-xs text-muted-foreground">{[nearest.postalCode, nearest.city].filter(Boolean).join(" ")}</div>
              )}
            </div>
            {typeof nearest.distanceKm === "number" && (
              <div className="shrink-0 text-right">
                <div className="font-semibold tabular-nums">
                  {t("tenderBoard.panel.place.distance", {
                    km: nearest.distanceKm.toLocaleString("de-DE", { maximumFractionDigits: 0 }),
                  })}
                </div>
                <div className="text-[11px] text-muted-foreground">{t("tenderBoard.panel.place.airline")}</div>
              </div>
            )}
          </div>
        ) : (
          <div className="text-xs text-muted-foreground">
            {location === undefined
              ? t("tenderBoard.loading")
              : location?.distancesWithheld
                ? t("tenderBoard.panel.place.tooCoarse")
                : t("tenderBoard.panel.place.noBranch")}
          </div>
        )}
        {location && (location.locations?.length ?? 0) > 0 && (
          <div className="mt-1 text-[11px] text-muted-foreground">
            {t("tenderBoard.locations.summary", {
              radius: location.radiusKm ?? 50,
              branches: location.locations?.length ?? 0,
              nearest: nearest?.label ?? "—",
              projects: location.projectCount ?? 0,
            })}
          </div>
        )}
      </Section>

      {mapResource ? (
        <Section
          title={t("tenderBoard.panel.place.mapTitle")}
          aside={
            <Button variant="ghost" size="sm" className="h-7 gap-1 text-xs" onClick={() => setMapLarge(true)}>
              <Maximize2 size={12} aria-hidden /> {t("tenderBoard.panel.place.enlarge")}
            </Button>
          }
        >
          <div className="overflow-hidden rounded-md border border-border">{map(260)}</div>
          <ul className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-muted-foreground" aria-label={t("tenderBoard.panel.place.legend")}>
            {Object.entries(layers).map(([key, layer]) => (
              <li key={key} className="flex items-center gap-1.5">
                {swatch(key)}{" "}
                {key === "tender"
                  ? t("tenderBoard.panel.place.legendPlace")
                  : key === "searchArea"
                    ? t("tenderBoard.panel.place.legendRadius", { radius: location?.radiusKm ?? 50 })
                    : (layer?.name ?? key)}
              </li>
            ))}
          </ul>
          <Dialog open={mapLarge} onOpenChange={setMapLarge}>
            <DialogContent className="max-w-5xl">
              <DialogHeader>
                <DialogTitle>{row.title}</DialogTitle>
              </DialogHeader>
              {mapLarge && map(600)}
            </DialogContent>
          </Dialog>
        </Section>
      ) : location !== undefined && !location?.location && !locationLoading ? (
        <div className="text-xs text-muted-foreground">{t("tenderBoard.locations.noMap")}</div>
      ) : null}
    </div>
  );
}
